import React from 'react';

import { useIntl } from 'react-intl-next';

import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import MicrophoneIcon from '@atlaskit/icon/core/microphone';
import Image from '@atlaskit/image';
import { ConfluenceLogo } from '@atlaskit/logo';
import { Modal } from '@atlassian/growth-pattern-library-modal';

import ConfluenceStandardOfferAIModalRightSideSVG from '../../assets/svg/ConfluenceStandardOfferAIModalRightSide.svg';
import ConfluenceStandardOfferAIModalRightSideSVGDark from '../../assets/svg/ConfluenceStandardOfferAIModalRightSideDark.svg';
import {
	Product,
	recordFirstModalShown,
	recordSecondModalShown,
	type StorageAdapter,
} from '../../utils';
import { ScreenEvent, useAnalytics } from '../../utils/analytics';
import {
	type BusinessLogicAnalyticsAttributes,
	type CommonAnalyticsAttributes,
	CONFLUENCE_STANDARD_OFFER_AI_MODAL,
	type ModalDismissalMethod,
	type ModalShownPreOrPostUpgrade,
	type OfferShownType,
	type SupportedProduct,
} from '../../utils/types';
import { useModalState } from '../../utils/useModalState';
import { EditionHighlightsLeftContent } from '../EditionHighlightsLeftContent';
import { PrimaryAndSecondaryCTAActionsGroup } from '../PrimaryAndSecondaryCTAActionsGroup';

import { messages } from './messages';

export const StandardOfferAIModalRightSide = () => (
	<Image
		src={ConfluenceStandardOfferAIModalRightSideSVG}
		srcDark={ConfluenceStandardOfferAIModalRightSideSVGDark}
		alt="Onboarding Strategy"
	/>
);

export const ConfluenceStandardOfferAIModal = ({
	analyticsAttributes,
	experienceUrl,
	storageAdapter,
	nthTimeShown = 1,
}: {
	analyticsAttributes: BusinessLogicAnalyticsAttributes;
	experienceUrl: string;
	nthTimeShown?: number;
	storageAdapter: StorageAdapter;
}) => {
	const intl = useIntl();

	const { isOpen, handleClose } = useModalState();

	const componentAttributes: CommonAnalyticsAttributes & BusinessLogicAnalyticsAttributes = {
		...analyticsAttributes,
		product: 'confluence' as SupportedProduct,
		offerShown: 'AI' as OfferShownType,
		modalShownPrePostUpgrade: 'PRE_UPGRADE' as ModalShownPreOrPostUpgrade,
		nthTimeShown,
	};

	const { clickedDismissModal, clickedPrimaryCTAOnModal } = useAnalytics();

	if (!isOpen) {
		return null;
	}

	const recordModalShown = () => {
		if (nthTimeShown === 1) {
			recordFirstModalShown(storageAdapter, Product.Confluence);
		} else if (nthTimeShown === 2) {
			recordSecondModalShown(storageAdapter, Product.Confluence);
		}
	};

	const handleDismissModal = (dismissalMethod: ModalDismissalMethod) => {
		recordModalShown();
		clickedDismissModal(CONFLUENCE_STANDARD_OFFER_AI_MODAL, {
			...componentAttributes,
			dismissalMethod,
		});
		handleClose();
	};

	const handlePrimaryCTAClick = () => {
		recordModalShown();
		clickedPrimaryCTAOnModal(CONFLUENCE_STANDARD_OFFER_AI_MODAL, componentAttributes);
		handleClose();
	};

	return (
		<Modal
			height="560px"
			logo={<ConfluenceLogo shouldUseNewLogoDesign size="xsmall" />}
			title={intl.formatMessage(messages.standardOfferAITitle)}
			subtitle={intl.formatMessage(messages.standardOfferAISubtitle)}
			testId="growth-pattern-library-modal"
			includeCloseButton
			onClose={() => {
				handleDismissModal('modalOverlayClick');
			}}
			sideContent={<StandardOfferAIModalRightSide />}
			actions={
				<PrimaryAndSecondaryCTAActionsGroup
					secondaryCTALabel={intl.formatMessage(messages.standardOfferAISecondaryCTA)}
					onSecondaryCTAClick={() => {
						handleDismissModal('secondaryCTAButton');
					}}
					primaryCTALabel={intl.formatMessage(messages.standardOfferAIPrimaryCTA)}
					onPrimaryCTAClick={handlePrimaryCTAClick}
					href={experienceUrl}
					isLoading={false}
				/>
			}
		>
			<EditionHighlightsLeftContent
				highlights={[
					{
						icon: AiGenerativeTextIcon,
						title: intl.formatMessage(messages.aiBenefitsNotesToDocs),
					},
					{
						icon: AiGenerativeTextSummaryIcon,
						title: intl.formatMessage(messages.aiBenefitsSummarizePages),
					},
					{
						icon: MicrophoneIcon,
						title: intl.formatMessage(messages.aiBenefitsTone),
					},
				]}
				disclaimerText={intl.formatMessage(messages.standardOfferAIDisclaimerText)}
			/>
			<ScreenEvent name="upgradeFreePoolTargetExperimentModal" attributes={componentAttributes} />
		</Modal>
	);
};
